import { Avatar as MantineAvatar, Skeleton, MantineSize } from "@mantine/core";
import { componentWrapper, polymorphicComponentWrapper } from "../../utilities";
import styles from "./avatar.module.scss";
import type { CustomAvatarFactory, CustomAvatarGroupFactory, IAvatarGroupProps, IProps } from "./avatar.types";
import { Children, cloneElement, isValidElement, useMemo } from "react";
import { Tooltip } from "../tooltip";

/**
 * Avatar component for displaying user profile pictures or initials.
 * @param {object} props.tooltip - Optional tooltip configuration to display on hover.
 * @returns {JSX.Element} The Avatar component.
 */
const Avatar = polymorphicComponentWrapper<CustomAvatarFactory, IProps>("Avatar", ({ tooltip, ...otherProps }, ref) => {
    const avatarElement = useMemo(
        () => (
            <MantineAvatar
                ref={ref}
                classNames={{ root: styles["root"], image: styles["image"], placeholder: styles["placeholder"] }}
                {...otherProps}
            />
        ),
        [ref, otherProps]
    );
    return tooltip ? <Tooltip {...tooltip}>{avatarElement}</Tooltip> : avatarElement;
});

/**
 * Avatar group component for displaying multiple avatars together.
 * @param {React.ReactNode} props.children - Avatar components to group together.
 * @param {string} props.size - The size of the avatars in the group.
 * @returns {JSX.Element} The AvatarGroup component.
 */
const AvatarGroup = componentWrapper<CustomAvatarGroupFactory, IAvatarGroupProps>(
    "AvatarGroup",
    ({ children, size, ...otherProps }, ref) => {
        return (
            <MantineAvatar.Group ref={ref} classNames={{ group: styles["group"] }} {...otherProps}>
                {Children.map(children, (child, index) => {
                    if (child === null) return null;
                    if (isValidElement(child) && child.type === Avatar) {
                        return cloneElement(child, { size });
                    } else {
                        return (
                            <Avatar key={index} data-has-custom-child={true} size={size}>
                                {child}
                            </Avatar>
                        );
                    }
                })}
            </MantineAvatar.Group>
        );
    }
);

/**
 * Skeleton loader component for Avatar.
 * @param {string} props.size - The size of the avatar skeleton.
 * @returns {JSX.Element} The AvatarSkeleton component.
 */
const AvatarSkeleton = ({ size }: { size: MantineSize }) => {
    return <Skeleton className={styles["skeleton"]} data-size={size} />;
};

Avatar.Group = AvatarGroup;
Avatar.Skeleton = AvatarSkeleton;

export { Avatar, AvatarGroup, AvatarSkeleton };
